import { useMemo, useState } from 'react';
import { Text, Pressable, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import { SectionCard } from '../../pages/AdminPanel/Products/formStyles';
import { useRemoteConfigContext } from '../../contexts/useRemoteConfigContext';
import { useCatalogProducts } from '../../queries/useCatalogProducts';
import { useProductCategories } from '../../queries/useProductCategories';
import {
  buildProductCategoryAiAssistantText,
  buildVitrineProductGroupsForAi,
  copyTextToClipboard,
} from '../../utils/buildProductCategoryAiAssistantText';

export default function ProductCategoryAiAssistant() {
  const { hasProductCatalog } = useRemoteConfigContext();
  const { data: catalogProducts = [], isLoading: isProductsLoading } = useCatalogProducts(
    hasProductCatalog
  );
  const { data: productCategories = [], isLoading: isCategoriesLoading } = useProductCategories();

  const [feedback, setFeedback] = useState('');

  const vitrineGroups = useMemo(
    () => buildVitrineProductGroupsForAi(catalogProducts, productCategories),
    [catalogProducts, productCategories]
  );

  const isLoading = isProductsLoading || isCategoriesLoading;
  const canGenerate = !isLoading && vitrineGroups.length > 0;

  if (!hasProductCatalog) return null;

  const handleGenerateWithAi = async () => {
    if (!canGenerate) return;

    const text = buildProductCategoryAiAssistantText(catalogProducts, productCategories);
    try {
      const copied = await copyTextToClipboard(text);
      setFeedback(
        copied
          ? 'Prompt copiado! Cole na IA da sua preferência (ChatGPT, Claude ou similar) para sugerir categorias. Capas: use Gerar capa com IA em cada produto.'
          : 'Não foi possível copiar. Tente novamente no navegador.'
      );
    } catch (error) {
      console.error('Erro ao copiar prompt de categorias:', error);
      setFeedback('Não foi possível copiar. Tente novamente no navegador.');
    }

    setTimeout(() => setFeedback(''), 5000);
  };

  const generateButton = canGenerate ? (
    <Pressable
      onPress={handleGenerateWithAi}
      style={({ hovered }) => [
        styles.generateBtn,
        hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
      ]}
    >
      <MaterialIcons name="auto-awesome" size={16} color={ADMIN_COLORS.accent} />
      <Text style={styles.generateBtnText}>Gerar com IA</Text>
    </Pressable>
  ) : null;

  return (
    <SectionCard
      title="Categorias de produtos na vitrine"
      subtitle="Use Gerar com IA para copiar a lista de produtos e um prompt sugerido da Blix para que a IA da sua preferência sugira categorias para a Vitrine de Produtos do seu app."
      right={generateButton}
      style={styles.card}
    >
      {feedback ? <Text style={styles.feedback}>{feedback}</Text> : null}
      {isLoading ? (
        <Text style={styles.hint}>Carregando produtos...</Text>
      ) : vitrineGroups.length === 0 ? (
        <Text style={styles.hint}>
          Habilite a Vitrine de Produtos na aba Funcionalidades e cadastre produtos na vitrine para
          gerar sugestões.
        </Text>
      ) : (
        <Text style={styles.hint}>
          Configure a categoria e a capa clicando no Produto abaixo → Vitrine de Produtos.{'\n\n'}
          As capas 16:9 são geradas produto a produto e também possuem a opção Gerar com IA.
        </Text>
      )}
    </SectionCard>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: 16,
    marginBottom: 4,
  },
  generateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: 'pointer',
  },
  generateBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.accent,
  },
  feedback: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.success,
    marginBottom: 8,
    lineHeight: 17,
  },
  hint: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    lineHeight: 18,
  },
});
